import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveTwin } from '../utils/twinStorage';
import './TwinSuccess.css';

interface TwinData {
  message: string;
  workspaceId: string;
  twinMakerWsId: string;
  factoryId: string;
  company: string;
  factoryType: string;
  machineCount: number;
  sensorCount: number;
}

const TwinSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [twinData, setTwinData] = useState<TwinData | null>(null);
  const [copying, setCopying] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Get twin data from navigation state
  useEffect(() => {
    const state = location.state as any;
    if (state?.workspaceId) {
      setTwinData(state);
      sessionStorage.setItem('twinData', JSON.stringify(state));
    } else if (state?.twinData) {
      setTwinData(state.twinData);
      sessionStorage.setItem('twinData', JSON.stringify(state.twinData));
    } else {
      const stored = sessionStorage.getItem('twinData');
      if (stored) {
        try {
          setTwinData(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse twin data:', e);
        }
      }
    }
  }, [location.state]);

  // Save twin to localStorage once when twinData is first received
  useEffect(() => {
    if (!twinData) return;

    // Save twin to localStorage (only once per twinData)
    console.log('Saving twin to localStorage:', twinData.company);
    saveTwin({
      name: twinData.company,
      company: twinData.company,
      factoryType: twinData.factoryType,
      machines: (twinData as any).machines || [],
      sensors: (twinData as any).sensors || [],
      workspaceId: twinData.workspaceId,
      twinMakerWsId: twinData.twinMakerWsId,
      factoryId: twinData.factoryId,
      machineCount: twinData.machineCount,
      sensorCount: twinData.sensorCount,
    });
  }, [twinData]); // Only run once when twinData changes

  // Auto-redirect after countdown
  useEffect(() => {
    if (!twinData) return;
    
    if (countdown <= 0) {
      navigate('/app');
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, twinData, navigate]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopying(true);
    setTimeout(() => setCopying(false), 2000);
  };

  const goToDashboard = () => {
    if (twinData) {
      const savedTwin = saveTwin({
        name: twinData.company,
        company: twinData.company,
        factoryType: twinData.factoryType,
        machines: (twinData as any).machines || [],
        sensors: (twinData as any).sensors || [],
        workspaceId: twinData.workspaceId,
        twinMakerWsId: twinData.twinMakerWsId,
        factoryId: twinData.factoryId,
        machineCount: twinData.machineCount,
        sensorCount: twinData.sensorCount,
      });
      navigate('/app', {
        state: { twinId: savedTwin.id }
      });
    }
  };

  if (!twinData) {
    return (
      <div className="twin-success-container">
        <div className="success-card error">
          <h2>⚠️ No Twin Data Found</h2>
          <p>Unable to load twin creation data.</p>
          <button onClick={() => navigate('/onboarding')} className="btn-primary">
            Back to Onboarding
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="twin-success-container">
      <div className="success-card">
        {/* Success Header */}
        <div className="success-header">
          <div className="success-icon">✓</div>
          <h1>Twin Created Successfully!</h1>
          <p className="subtitle">Your digital twin is ready to use</p>
        </div>

        {/* Twin Details */}
        <div className="twin-details">
          <div className="detail-section">
            <h3>📋 Configuration Summary</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Company</label>
                <div className="detail-value">
                  <span>{twinData.company}</span>
                  <button 
                    className="btn-copy" 
                    onClick={() => copyToClipboard(twinData.company)}
                    title="Copy"
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="detail-item">
                <label>Factory Type</label>
                <div className="detail-value">
                  <span className="factory-type">{twinData.factoryType.toUpperCase()}</span>
                </div>
              </div>

              <div className="detail-item">
                <label>Workspace ID</label>
                <div className="detail-value">
                  <code>{twinData.workspaceId}</code>
                  <button 
                    className="btn-copy" 
                    onClick={() => copyToClipboard(twinData.workspaceId)}
                    title="Copy"
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="detail-item">
                <label>Twin Maker Workspace</label>
                <div className="detail-value">
                  <code>{twinData.twinMakerWsId}</code>
                  <button 
                    className="btn-copy" 
                    onClick={() => copyToClipboard(twinData.twinMakerWsId)}
                    title="Copy"
                  >
                    📋
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Resources */}
          <div className="detail-section">
            <h3>⚙️ Resources Configured</h3>
            <div className="resources-grid">
              <div className="resource-card">
                <div className="resource-count">{twinData.machineCount}</div>
                <div className="resource-label">Machines</div>
              </div>
              <div className="resource-card">
                <div className="resource-count">{twinData.sensorCount}</div>
                <div className="resource-label">Sensors</div>
              </div>
            </div>
          </div>

          {/* API Response */}
          <div className="detail-section">
            <h3>📡 API Response</h3>
            <div className="code-block">
              <pre>{JSON.stringify(twinData, null, 2)}</pre>
              <button 
                className="btn-copy-large" 
                onClick={() => copyToClipboard(JSON.stringify(twinData, null, 2))}
              >
                {copying ? '✓ Copied!' : 'Copy JSON'}
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            className="btn-secondary" 
            onClick={() => navigate('/onboarding')}
          >
            ← Back to Onboarding
          </button>
          <button 
            className="btn-primary btn-large" 
            onClick={goToDashboard}
          >
            Go to Dashboard →
          </button>
        </div>

        {/* Countdown Timer */}
        <div className="countdown-section">
          <p>Auto-redirect to Dashboard in <span className="countdown-timer">{countdown}</span> seconds...</p>
        </div>

        {/* Info Footer */}
        <div className="info-footer">
          <p>
            💡 Your workspace is now active in AWS IoT TwinMaker. 
            The digital twin will be available for monitoring and analysis.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwinSuccess;
